<template>
  <div :class="['sidebar', { collapsed: collapsed }]">
    <div class="sidebar-inner">
      <div class="sidebar-toggle-wrapper">
        <button class="toggle-button" @click="toggleSidebar">
          <img :src="collapsed
            ? require('@/assets/icons/sidebar-expand.svg')
            : require('@/assets/icons/sidebar-collapse.svg')" alt="Toggle Sidebar" class="toggle-icon icon">
        </button>
      </div>

      <div class="top-section">

        <div class="new-chat-container">
          <button class="new-chat" @click="startNewChat">
            <div class="new-chat-content">
              <img
                :src="require('@/assets/icons/new-chat.svg')"
                alt="Plus Icon" class="icon" />
              <span>New chat</span>
            </div>
          </button>
        </div>

        <div class="search-container">
          <button class="search" @click="toggleSearchBar">
            <div class="search-content">
              <img :src="require('@/assets/icons/search-light-theme.svg')"
                alt="Search Icon" class="icon">
              <span>Search Chats</span>
            </div>
          </button>
        </div>

        <div v-if="showSearchBar" class="search-bar">
          <input
          v-model="searchQuery"
          placeholder="Search Conversations..."
          class="search-input">
          <button class="clear-btn" @click="clearSearch">
            <img :src="require('@/assets/icons/clear-btn-light-theme.svg')"
              alt="Clear Search" class="clear-icon">
          </button>
        </div>

        <!-- Projects Section -->
        <div class="projects-section" v-if="!collapsed">
          <div class="section-header" @click="toggleProjectsExpanded">
            <span class="section-label">Projects</span>
            <span class="section-toggle">{{ projectsExpanded ? '▾' : '▸' }}</span>
          </div>
          <div v-if="projectsExpanded" class="projects-list">
            <div v-for="project in projects" :key="project.id">
              <div
                class="project-item"
                :class="{ 'selected': selectedProjectId === project.id }"
                @click="selectProject(project)"
              >
                <img :src="require('@/assets/icons/project-icon.svg')" alt="Project" class="icon project-icon" />
                <div class="project-title">{{ project.name }}</div>
                <span v-if="project.is_pinned" class="pin-indicator" title="Pinned">📌</span>
                <button
                  class="project-action-btn"
                  @click.stop="toggleProjectMenu(project.id)"
                  title="Project actions"
                >⋯</button>
                <!-- Project context menu -->
                <div
                  v-if="projectMenuId === project.id"
                  class="project-context-menu"
                  @click.stop
                >
                  <button class="context-menu-item" @click="openEditProject(project)">
                    ✏️ Edit
                  </button>
                  <button class="context-menu-item" @click="togglePin(project)">
                    {{ project.is_pinned ? '📌 Unpin' : '📌 Pin' }}
                  </button>
                  <button class="context-menu-item" @click="toggleArchive(project)">
                    📦 Archive
                  </button>
                  <button class="context-menu-item context-menu-item-danger" @click="confirmDeleteProject(project)">
                    🗑️ Delete
                  </button>
                </div>
              </div>
            </div>
            <button class="new-project-btn" @click="showNewProjectModal = true">
              <img :src="require('@/assets/icons/plus-icon.svg')" alt="New Project" class="icon-sm" />
              <span>New Project</span>
            </button>
            <button v-if="archivedProjects.length > 0" class="archived-toggle-btn" @click="showArchivedProjects = !showArchivedProjects">
              📦 {{ showArchivedProjects ? 'Hide' : 'Show' }} Archived ({{ archivedProjects.length }})
            </button>
            <template v-if="showArchivedProjects">
              <div v-for="project in archivedProjects" :key="'archived-' + project.id" class="project-item archived-project-item"
                @click="selectProject(project)"
                :class="{ 'selected': selectedProjectId === project.id }"
              >
                <img :src="require('@/assets/icons/project-icon.svg')" alt="Project" class="icon project-icon" />
                <div class="project-title">{{ project.name }}</div>
                <button class="project-action-btn" @click.stop="unarchiveProject(project)" title="Unarchive">📦</button>
              </div>
            </template>
          </div>
        </div>

        <!-- Conversations Section -->
        <div class="conversations-section">
          <div class="section-header" v-if="!collapsed">
            <span class="section-label">Chats</span>
          </div>
          <div class="conversations">
            <div v-for="conversation in filteredConversations" :key="conversation.id" class="conversation-item"
              @click="loadConversation(conversation)"
              :class="{ 'selected': conversation.id === selectedConversationId }">
              <img
                :src="require('@/assets/icons/chat-icon.svg')"
                alt="Chat Icon" class="icon" />

              <div class="conversation-title">

                <template v-if="editingId === conversation.id">
                  <input
                    v-model="tempName"
                    v-autofocus
                    :ref="`rename_${conversation.id}`"
                    @keyup.enter="renameConversation(conversation)"
                    @blur="cancelEdit()"
                    class="rename-input"
                    :placeholder="conversation.name"
                    autofocus
                  />
                </template>
                <template v-else>
                  {{ conversation.name }}
                </template>
              </div>

              <button
                v-if="editingId !== conversation.id"
                class="chat-kebab-btn"
                @click.stop="toggleChatMenu(conversation.id)"
                title="Chat options"
              >⋮</button>
              <!-- Chat context menu -->
              <div
                v-if="chatMenuId === conversation.id"
                class="chat-context-menu"
                @click.stop
              >
                <button class="context-menu-item" @click="startEditFromMenu(conversation)">
                  ✏️ Rename
                </button>
                <button v-if="projects.length > 0" class="context-menu-item" @click="openMoveToProjectFromMenu(conversation)">
                  📂 Move to Project
                </button>
                <button class="context-menu-item context-menu-item-danger" @click="deleteChat(conversation)">
                  🗑️ Delete Chat
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>

    </div>

    <!-- New Project Modal -->
    <div v-if="showNewProjectModal" class="modal-overlay" @click.self="closeNewProjectModal">
      <div class="modal-content">
        <h3 class="modal-title">Create New Project</h3>
        <div class="form-group">
          <label for="project-name">Project Name</label>
          <input
            id="project-name"
            v-model="newProjectName"
            type="text"
            class="form-input"
            placeholder="Enter project name"
            @keyup.enter="createProject"
            ref="projectNameInput"
          />
        </div>
        <div class="form-group">
          <label for="project-description">Description (optional)</label>
          <textarea
            id="project-description"
            v-model="newProjectDescription"
            class="form-input form-textarea"
            placeholder="Enter project description"
            rows="3"
          ></textarea>
        </div>
        <div v-if="newProjectError" class="form-error">{{ newProjectError }}</div>
        <div class="modal-actions">
          <button class="btn btn-cancel" @click="closeNewProjectModal">Cancel</button>
          <button class="btn btn-create" @click="createProject" :disabled="!newProjectName.trim() || creatingProject">
            {{ creatingProject ? 'Creating...' : 'Create Project' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Move to Project Modal -->
    <div v-if="showMoveToProjectModal" class="modal-overlay" @click.self="closeMoveToProject">
      <div class="modal-content">
        <h3 class="modal-title">Move Chat to Project</h3>
        <p class="move-chat-name">{{ moveTargetConversation?.name }}</p>
        <div class="project-select-list">
          <div
            v-for="proj in projects"
            :key="'move-' + proj.id"
            class="project-select-item"
            :class="{ 'current-project': moveTargetConversation?.project === proj.id }"
            @click="moveConversationToProject(proj.id)"
          >
            <img :src="require('@/assets/icons/project-icon.svg')" alt="Project" class="icon project-icon" />
            <span class="project-select-name">{{ proj.name }}</span>
            <span v-if="moveTargetConversation?.project === proj.id" class="current-badge">Current</span>
          </div>
          <div v-if="projects.length === 0" class="project-select-empty">
            No projects available. Create a project first.
          </div>
        </div>
        <div class="modal-actions">
          <button class="btn btn-cancel" @click="closeMoveToProject">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Edit Project Modal -->
    <div v-if="showEditProjectModal" class="modal-overlay" @click.self="closeEditProjectModal">
      <div class="modal-content">
        <h3 class="modal-title">Edit Project</h3>
        <div class="form-group">
          <label for="edit-project-name">Project Name</label>
          <input
            id="edit-project-name"
            v-model="editProjectName"
            type="text"
            class="form-input"
            placeholder="Enter project name"
            @keyup.enter="saveEditProject"
            ref="editProjectNameInput"
          />
        </div>
        <div class="form-group">
          <label for="edit-project-description">Description (optional)</label>
          <textarea
            id="edit-project-description"
            v-model="editProjectDescription"
            class="form-input form-textarea"
            placeholder="Enter project description"
            rows="3"
          ></textarea>
        </div>
        <div v-if="editProjectError" class="form-error">{{ editProjectError }}</div>
        <div class="modal-actions">
          <button class="btn btn-cancel" @click="closeEditProjectModal">Cancel</button>
          <button class="btn btn-create" @click="saveEditProject" :disabled="!editProjectName.trim() || savingProject">
            {{ savingProject ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Project Confirmation Modal -->
    <div v-if="showDeleteProjectModal" class="modal-overlay" @click.self="closeDeleteProjectModal">
      <div class="modal-content">
        <h3 class="modal-title">Delete Project</h3>
        <p class="delete-warning">
          Are you sure you want to delete <strong>{{ deleteTargetProject?.name }}</strong>?
        </p>
        <p class="delete-details">
          This will permanently delete the project and its documents. Chats in this project will become standalone conversations.
        </p>
        <div class="modal-actions">
          <button class="btn btn-cancel" @click="closeDeleteProjectModal">Cancel</button>
          <button class="btn btn-danger" @click="deleteProject" :disabled="deletingProject">
            {{ deletingProject ? 'Deleting...' : 'Delete Project' }}
          </button>
        </div>
      </div>
    </div>

    <div class="bottom-section">
      <div class="menu-item" @click="$emit('show-memories')">
        <span class="memory-icon icon"></span>
        <span>Memories</span>
      </div>

      <!-- Company Knowledge hidden per management request
      <div v-if="currentUser && currentUser.is_staff" class="menu-item" @click="$emit('show-knowledge')">
        <span class="knowledge-icon icon"></span>
        <span>Company Knowledge</span>
      </div>
      -->

      <div class="menu-item" @click="toggleTheme">
        <span class="theme-icon icon"></span>
        <span>{{ currentTheme.includes('dark')  ? 'Light Theme' : 'Dark Theme' }}</span>
      </div>

      <a v-if="companyInformation && companyInformation.feedback_url" class="menu-item feedback-link" :href="companyInformation.feedback_url" target="_blank" rel="noopener noreferrer">
        <span class="feedback-icon icon"></span>
        <span>Feedback</span>
      </a>

      <div v-show="false" class="menu-item" @click="$emit('show-faq')">
        <img :src="require('@/assets/icons/faq-icon.svg')"
          alt="Updates & FAQ Icon" />
        Updates & FAQ
      </div>

      <div class="menu-item" @click="$emit('logout')">
        <img
          :src="require('@/assets/icons/logout-icon.svg')"
          alt="Log out Icon" class="icon"/>
        <span>Log Out</span>
      </div>

    </div>
  </div>
</template>

<script>
import api from '@/axios-config';
import { getCurrentTheme, toggleTheme as toggleThemeUtil } from '@/utils/themeManager';

export default {
  directives: {
    autofocus: {
      inserted(el) { el.focus(); el.select && el.select(); },
      mounted(el) { el.focus(); el.select && el.select(); },
    }
  },
  name: 'AppSidebar',
  props: { conversations: Array, conversationMessagesMap: Object, companyInformation: Object, currentUser: Object },
  data() {
    return {
      selectedConversationId: null,
      collapsed: false,
      showSearchBar: false,
      searchQuery: '',
      editingId: null,
      tempName: '',
      screenWidth: window.innerWidth,
      currentTheme: getCurrentTheme(),
      // Projects
      projects: [],
      selectedProjectId: null,
      projectsExpanded: true,
      showNewProjectModal: false,
      newProjectName: '',
      newProjectDescription: '',
      newProjectError: '',
      creatingProject: false,
      // Project detail
      projectDocuments: [],
      projectConversations: [],
      uploadingDocument: false,
      // Project actions
      projectMenuId: null,
      archivedProjects: [],
      showArchivedProjects: false,
      // Chat context menu
      chatMenuId: null,
      // Move to project
      showMoveToProjectModal: false,
      moveTargetConversation: null,
      // Edit project
      showEditProjectModal: false,
      editProjectId: null,
      editProjectName: '',
      editProjectDescription: '',
      editProjectError: '',
      savingProject: false,
      // Delete project
      showDeleteProjectModal: false,
      deleteTargetProject: null,
      deletingProject: false,
    };
  },
  computed: {
    filteredConversations() {
      // First, filter out conversations that belong to projects
      const nonProjectConversations = this.conversations.filter(convo => !convo.project);

      // Then apply search filter if there's a query
      if (!this.searchQuery) return nonProjectConversations;

      const query = this.searchQuery.toLowerCase();

      return nonProjectConversations.filter((convo) => {
        const titleMatch = convo.name?.toLowerCase().includes(query);

        const messages = this.conversationMessagesMap?.[convo.resource_id] || [];

        const historyMatch = messages.some((msg) => {
          const content = msg?.html_content ?? msg?.content ?? '';
          return content.toLowerCase().includes(query);
        });

        return titleMatch || historyMatch;
      });
    }
  },
  methods: {
    startEdit(convo) {
      this.editingId = convo.id;
      this.tempName = convo.name;
      this.$nextTick(() => {
        const el = this.$refs[`rename_${convo.id}`];
        if (el) el.select();
      });
    },
    cancelEdit() {
      this.editingId = null;
      this.tempName = '';
    },

    async renameConversation(convo) {
      const newName = (this.tempName || '').trim();
      if (!newName || newName === convo.name) {
        this.cancelEdit();
        return;
      }

      await api.patch(`/api/conversation/${convo.id}/rename/`, { name: newName });
      this.$emit('refresh-conversations');

      this.cancelEdit();
    },

    // --- Project Methods ---
    async loadProjects() {
      try {
        const response = await api.get('/api/projects/');
        this.projects = response.data;
      } catch (err) {
        console.error('Failed to load projects:', err);
      }
    },
    toggleProjectsExpanded() {
      this.projectsExpanded = !this.projectsExpanded;
    },
    selectProject(project) {
      if (this.selectedProjectId === project.id) {
        // Deselect if clicking same project
        this.selectedProjectId = null;
        this.projectDocuments = [];
        this.projectConversations = [];
        return;
      }
      this.selectedProjectId = project.id;
      this.loadProjectDetail(project.id);
      // Emit to show project view in main content area
      this.$emit('show-project-view', project);
    },
    async loadProjectDetail(projectId) {
      try {
        const response = await api.get(`/api/projects/${projectId}/`);
        this.projectDocuments = response.data.documents || [];
        // Load conversations that belong to this project
        this.projectConversations = (this.conversations || []).filter(
          c => c.project === projectId
        );
      } catch (err) {
        console.error('Failed to load project detail:', err);
        this.projectDocuments = [];
        this.projectConversations = [];
      }
    },
    startProjectChat(project) {
      this.$emit('new-chat-in-project', project);
    },
    async handleDocumentUpload(event, projectId) {
      const file = event.target.files[0];
      if (!file) return;
      this.uploadingDocument = true;
      try {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/api/projects/${projectId}/documents/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        await this.loadProjectDetail(projectId);
        // Update the project document count in the list
        const proj = this.projects.find(p => p.id === projectId);
        if (proj) proj.document_count = this.projectDocuments.length;
      } catch (err) {
        console.error('Failed to upload document:', err);
        alert('Failed to upload document. Please try again.');
      } finally {
        this.uploadingDocument = false;
        // Reset the file input
        event.target.value = '';
      }
    },
    async deleteDocument(projectId, docId) {
      try {
        await api.delete(`/api/projects/${projectId}/documents/${docId}/`);
        await this.loadProjectDetail(projectId);
        const proj = this.projects.find(p => p.id === projectId);
        if (proj) proj.document_count = this.projectDocuments.length;
      } catch (err) {
        console.error('Failed to delete document:', err);
      }
    },
    async createProject() {
      const name = this.newProjectName.trim();
      if (!name) return;
      this.creatingProject = true;
      this.newProjectError = '';
      try {
        const response = await api.post('/api/projects/', {
          name: name,
          description: this.newProjectDescription.trim(),
        });
        this.projects.unshift(response.data);
        this.closeNewProjectModal();
        this.$emit('project-created', response.data);
      } catch (err) {
        console.error('Failed to create project:', err);
        this.newProjectError = err.response?.data?.name?.[0] || err.response?.data?.detail || 'Failed to create project. Please try again.';
      } finally {
        this.creatingProject = false;
      }
    },
    closeNewProjectModal() {
      this.showNewProjectModal = false;
      this.newProjectName = '';
      this.newProjectDescription = '';
      this.newProjectError = '';
    },
    // --- Pin/Archive Methods ---
    toggleProjectMenu(projectId) {
      if (this.projectMenuId === projectId) {
        this.projectMenuId = null;
      } else {
        this.projectMenuId = projectId;
      }
    },
    closeProjectMenu() {
      this.projectMenuId = null;
    },
    async togglePin(project) {
      try {
        const response = await api.post(`/api/projects/${project.id}/pin/`);
        project.is_pinned = response.data.is_pinned;
        this.closeProjectMenu();
        // Re-sort projects: pinned first, then by updated_at
        this.projects.sort((a, b) => {
          if (a.is_pinned !== b.is_pinned) return b.is_pinned ? 1 : -1;
          return new Date(b.updated_at) - new Date(a.updated_at);
        });
      } catch (err) {
        console.error('Failed to toggle pin:', err);
      }
    },
    async toggleArchive(project) {
      try {
        const response = await api.post(`/api/projects/${project.id}/archive/`);
        if (response.data.is_archived) {
          // Move from active to archived
          this.projects = this.projects.filter(p => p.id !== project.id);
          project.is_archived = true;
          this.archivedProjects.push(project);
          if (this.selectedProjectId === project.id) {
            this.selectedProjectId = null;
            this.projectDocuments = [];
            this.projectConversations = [];
          }
        } else {
          // Move from archived to active (shouldn't happen from this button, but handle it)
          project.is_archived = false;
          this.archivedProjects = this.archivedProjects.filter(p => p.id !== project.id);
          this.projects.push(project);
          this.projects.sort((a, b) => {
            if (a.is_pinned !== b.is_pinned) return b.is_pinned ? 1 : -1;
            return new Date(b.updated_at) - new Date(a.updated_at);
          });
        }
        this.closeProjectMenu();
      } catch (err) {
        console.error('Failed to toggle archive:', err);
      }
    },
    async unarchiveProject(project) {
      try {
        const response = await api.post(`/api/projects/${project.id}/archive/`);
        if (!response.data.is_archived) {
          project.is_archived = false;
          this.archivedProjects = this.archivedProjects.filter(p => p.id !== project.id);
          this.projects.push(project);
          this.projects.sort((a, b) => {
            if (a.is_pinned !== b.is_pinned) return b.is_pinned ? 1 : -1;
            return new Date(b.updated_at) - new Date(a.updated_at);
          });
        }
      } catch (err) {
        console.error('Failed to unarchive project:', err);
      }
    },
    // --- Edit Project Methods ---
    openEditProject(project) {
      this.editProjectId = project.id;
      this.editProjectName = project.name;
      this.editProjectDescription = project.description || '';
      this.editProjectError = '';
      this.showEditProjectModal = true;
      this.closeProjectMenu();
      this.$nextTick(() => {
        if (this.$refs.editProjectNameInput) {
          this.$refs.editProjectNameInput.focus();
          this.$refs.editProjectNameInput.select();
        }
      });
    },
    closeEditProjectModal() {
      this.showEditProjectModal = false;
      this.editProjectId = null;
      this.editProjectName = '';
      this.editProjectDescription = '';
      this.editProjectError = '';
    },
    async saveEditProject() {
      const name = this.editProjectName.trim();
      if (!name) return;
      this.savingProject = true;
      this.editProjectError = '';
      try {
        const response = await api.patch(`/api/projects/${this.editProjectId}/`, {
          name: name,
          description: this.editProjectDescription.trim(),
        });
        // Update local project data
        const project = this.projects.find(p => p.id === this.editProjectId);
        if (project) {
          project.name = response.data.name;
          project.description = response.data.description;
        }
        this.closeEditProjectModal();
      } catch (err) {
        console.error('Failed to update project:', err);
        this.editProjectError = err.response?.data?.name?.[0] || err.response?.data?.detail || 'Failed to update project. Please try again.';
      } finally {
        this.savingProject = false;
      }
    },
    // --- Delete Project Methods ---
    confirmDeleteProject(project) {
      this.deleteTargetProject = project;
      this.showDeleteProjectModal = true;
      this.closeProjectMenu();
    },
    closeDeleteProjectModal() {
      this.showDeleteProjectModal = false;
      this.deleteTargetProject = null;
    },
    async deleteProject() {
      if (!this.deleteTargetProject) return;
      this.deletingProject = true;
      try {
        await api.delete(`/api/projects/${this.deleteTargetProject.id}/`);
        // Remove from local list
        this.projects = this.projects.filter(p => p.id !== this.deleteTargetProject.id);
        this.archivedProjects = this.archivedProjects.filter(p => p.id !== this.deleteTargetProject.id);
        // Deselect if this was the selected project
        if (this.selectedProjectId === this.deleteTargetProject.id) {
          this.selectedProjectId = null;
          this.projectDocuments = [];
          this.projectConversations = [];
        }
        // Refresh conversations list since chats become standalone
        this.$emit('refresh-conversations');
        this.closeDeleteProjectModal();
      } catch (err) {
        console.error('Failed to delete project:', err);
        alert('Failed to delete project. Please try again.');
      } finally {
        this.deletingProject = false;
      }
    },
    async loadArchivedProjects() {
      try {
        const response = await api.get('/api/projects/?archived=true');
        this.archivedProjects = response.data;
      } catch (err) {
        console.error('Failed to load archived projects:', err);
      }
    },
    // --- Chat Context Menu Methods ---
    toggleChatMenu(conversationId) {
      this.chatMenuId = this.chatMenuId === conversationId ? null : conversationId;
    },
    closeChatMenu() {
      this.chatMenuId = null;
    },
    startEditFromMenu(conversation) {
      this.closeChatMenu();
      this.startEdit(conversation);
    },
    openMoveToProjectFromMenu(conversation) {
      this.closeChatMenu();
      this.openMoveToProject(conversation);
    },
    deleteChat(conversation) {
      this.closeChatMenu();
      this.$emit('clear-conversation', conversation.id);
      if (this.selectedConversationId === conversation.id) {
        this.selectedConversationId = null;
      }
    },
    // --- Move to Project Methods ---
    openMoveToProject(conversation) {
      this.moveTargetConversation = conversation;
      this.showMoveToProjectModal = true;
    },
    closeMoveToProject() {
      this.showMoveToProjectModal = false;
      this.moveTargetConversation = null;
    },
    async removeConversationFromProject(conversation) {
      try {
        await api.post(`/api/conversations/${conversation.id}/move/`, {
          project_id: null,
        });
        // Update local conversation data
        const previousProjectId = conversation.project;
        conversation.project = null;
        // Refresh project detail if viewing that project
        if (this.selectedProjectId === previousProjectId) {
          await this.loadProjectDetail(previousProjectId);
        }
        // Refresh conversations list
        this.$emit('refresh-conversations');
      } catch (err) {
        console.error('Failed to remove conversation from project:', err);
        alert('Failed to remove conversation from project. Please try again.');
      }
    },
    async moveConversationToProject(projectId) {
      if (!this.moveTargetConversation) return;
      // Don't move if already in this project
      if (this.moveTargetConversation.project === projectId) {
        this.closeMoveToProject();
        return;
      }
      try {
        await api.post(`/api/conversations/${this.moveTargetConversation.id}/move/`, {
          project_id: projectId,
        });
        // Update local conversation data
        this.moveTargetConversation.project = projectId;
        // Refresh project detail if viewing that project
        if (this.selectedProjectId === projectId) {
          await this.loadProjectDetail(projectId);
        }
        // Refresh conversations list
        this.$emit('refresh-conversations');
        this.closeMoveToProject();
      } catch (err) {
        console.error('Failed to move conversation to project:', err);
        alert('Failed to move conversation. Please try again.');
      }
    },
    getFirstMessage(conversation) {
      const messages = this.conversationMessagesMap?.[conversation.resource_id] || [];
      return messages.length ? messages[0].content : '';
    },
    clearSearch() {
      this.searchQuery = '';
    },
    toggleSearchBar() {
      this.openSidebar();
      this.showSearchBar = !this.showSearchBar;
      this.searchQuery = '';
    },
    loadConversation(conversation) {
      this.selectedConversationId = conversation.id;
      this.$emit('load-conversation', conversation);
    },
    startNewChat() {
      console.log("Sidebar: New Chat clicked, resetting conversation.");
      this.selectedConversationId = null;
      this.$emit('new-chat');
    },
    toggleTheme() {
      this.currentTheme = toggleThemeUtil();
      this.$emit('update-logo');
    },
    toggleSidebar() {
      this.collapsed = !this.collapsed;
      this.$emit('collapsed', this.collapsed);
    },
    openSidebar() {
      if (this.collapsed) {
        this.collapsed = false;
        this.$emit('collapsed', false);
      }
    },
    handleCollapse(val) {
      this.collapsed = val;
    },

    // Dynamically collapse the sidebar based on the screen width
    handleResize() {
      this.screenWidth = window.innerWidth;

      // Collapse the sidebar if the width is less than 768px
      if (this.screenWidth < 768) {
        this.collapsed = true;
      } else {
        this.collapsed = false;
      }
      
      this.$emit('collapsed', this.collapsed);
    },
  },
  watch: {
    showNewProjectModal(val) {
      if (val) {
        this.$nextTick(() => {
          if (this.$refs.projectNameInput) {
            this.$refs.projectNameInput.focus();
          }
        });
      }
    },
    showEditProjectModal(val) {
      if (val) {
        this.$nextTick(() => {
          if (this.$refs.editProjectNameInput) {
            this.$refs.editProjectNameInput.focus();
            this.$refs.editProjectNameInput.select();
          }
        });
      }
    },
  },
  mounted() {
    // Add an event listener to watch for window resizing
    window.addEventListener('resize', this.handleResize);

    // Initial resize check on component mount
    this.handleResize();

    // Load projects
    this.loadProjects();
    this.loadArchivedProjects();

    // Close context menus when clicking outside
    this._closeMenuHandler = () => {
      if (this.projectMenuId !== null) {
        this.closeProjectMenu();
      }
      if (this.chatMenuId !== null) {
        this.closeChatMenu();
      }
    };
    document.addEventListener('click', this._closeMenuHandler);
  },
  beforeUnmount() {
    // Clean up the event listener
    window.removeEventListener('resize', this.handleResize);
    if (this._closeMenuHandler) {
      document.removeEventListener('click', this._closeMenuHandler);
    }
  },
};
</script>

<style scoped>
.sidebar {
  width: 20rem;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-right: 1px solid rgba(28, 28, 28, 0.1);
  padding: 20px;
  box-sizing: border-box;
  font-family: Inter, Helvetica, sans-serif;
  z-index: 2;
  transition: width 0.3s ease;
  /* Ensures it stays above the footer */
}

.sidebar-inner {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.sidebar-toggle-wrapper {
  display: flex;
  justify-content: flex-end;
  padding: 0.5rem 1rem 0.5rem 0.5rem;
  width: 100%;
}

.sidebar.collapsed {
  width: 60px;
}

.sidebar.collapsed .conversation-item {
  display: none;
}

.sidebar.collapsed .new-chat span {
  display: none;
}

.sidebar.collapsed .search span {
  display: none;
}

.sidebar.collapsed .search .icon {
  margin: 0;
  margin-right: 0;
  padding: 0;
}

.sidebar.collapsed .new-chat {
  height: 40px;
  padding: 0;
  background: transparent;
}

.sidebar.collapsed .new-chat .icon {
  margin: 0;
}

.sidebar.collapsed .search-bar {
  display: none;
}

.sidebar.collapsed .menu-item span:not(.icon) {
  display: none;
}

.sidebar.collapsed .search-container:hover {
  background-color: transparent;
}

.toggle-button {
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: 18px;
  margin-left: 3px;
}

.toggle-icon {
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.top-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.conversations-section {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  gap: 20px;
}

.conversations-section::-webkit-scrollbar {
  width: 8px;
}

.conversations-section::-webkit-scrollbar-track {
  background: transparent;
}

.conversations-section::-webkit-scrollbar-thumb {
  background-color: #555;
  border-radius: 10px;
}

.conversations-section::-webkit-scrollbar-thumb:hover {
  background-color: #888;
}


.new-chat-container {
  position: sticky;
  top: 0;
  z-index: 1;
}

.new-chat {
  width: 100%;
  height: 40px;
  border: none;
  padding: 8px 0px;
  background: white;
  color: black;
  font-size: 0.88rem;
  transition: all .3s cubic-bezier(.05, .03, .35, 1);
  background-image: linear-gradient(#8ECAE6 0, #8ECAE6 100%);
  border-radius: 5px;
  cursor: pointer;
  margin-top: 15px;
  opacity: 1;
}

.new-chat:hover {
  opacity: 0.85;
}

.new-chat-content {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
}

.new-chat .icon {
  width: 24px;
  height: 24px;
  margin-right: 8px;
}

.search-container {
  padding-top: 5px;
  padding-bottom: 5px;
}
.search {
  background: none;
  display: flex;
  align-items: center;
  cursor: pointer;
  border: none;
  font-size: 0.88rem;
  color: black;
  padding: 0;
}

.search-content {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0;
}

.search-container:hover {
  background-color: #e4e4e4;
  border-radius: 5px;
}

.search .icon {
  width: 24px;
  height: 24px;
  margin-right: 5px;
}

.search-bar {
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding: 0;
  padding-right: 15px;
  border-radius: 4px;
  border: 1px solid #ccc;
  outline: none;
  background-color: white;
  color: black;
}

.search-input {
  width: 100%;
  padding: 8px;
  padding-right: 16px;
  box-sizing: border-box;
  border: none;
  background-color: transparent;
  border-radius: 4px;
  outline: none;
}

.clear-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  z-index: 1;
}

.clear-icon {
  width: 16px;
  height: 16px;
  opacity: 0.7;
}

.clear-icon:hover {
  opacity: 1;
}

.conversations .icon {
  width: 24px;
  height: 24px;
  margin-right: 5px;
}

.conversations {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.conversation-item {
  position: relative;
  display: flex;
  align-items: center;
  padding: 8px 0px;
  font-size: 0.88rem;
  color: #1C1C1C;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-radius: 5px;
}

.conversation-item:hover {
  background-color: #d3d3d3;
}

.conversation-item.selected {
  background-color: #E8F0FE;
  /* Highlight color */
  border-radius: 8px;
}

.conversation-title {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  /* Keep text on single line */
  overflow: hidden;
  /* Hide overflow text */
  text-overflow: ellipsis;
  /* Show ellipsis for truncated text */
  line-height: 1.3;
}

.rename-input {
  display: block;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding:6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  line-height: 1.2;
  background-color: white;
  color: black;
}

.rename-input:focus {
  outline: none;
}

.bottom-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 20px;
  flex-shrink: 0;
  border-top: 1px solid rgba(28, 28, 28, 0.1);
  /* Ensure the top border is visible */
  background-color: white;
  /* Ensure background matches the sidebar */
  z-index: 1;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.88rem;
  color: #1C1C1C;
  cursor: pointer;
}

.menu-item img {
  width: 24px;
  height: 24px;
}

/* Theme icon - styled via CSS based on current theme */
.theme-icon {
  display: inline-block;
  width: 24px;
  height: 24px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  background-image: url('@/assets/icons/light-mode-icon.svg');
}

[data-theme="dark"] .theme-icon {
  background-image: url('@/assets/icons/dark-mode-icon.svg');
}

/* Memory icon - styled via CSS based on current theme */
.memory-icon {
  display: inline-block;
  width: 24px;
  height: 24px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  /* Use a brain/memory emoji as background via SVG data URI for light theme */
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7z'/%3E%3Cpath d='M9 21h6'/%3E%3Cpath d='M10 17v4'/%3E%3Cpath d='M14 17v4'/%3E%3C/svg%3E");
}

[data-theme="dark"] .memory-icon {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23ccc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7z'/%3E%3Cpath d='M9 21h6'/%3E%3Cpath d='M10 17v4'/%3E%3Cpath d='M14 17v4'/%3E%3C/svg%3E");
}

/* Knowledge icon (book) - admin only */
.knowledge-icon {
  display: inline-block;
  width: 24px;
  height: 24px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M4 19.5A2.5 2.5 0 0 1 6.5 17H20'/%3E%3Cpath d='M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z'/%3E%3C/svg%3E");
}

[data-theme="dark"] .knowledge-icon {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23ccc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M4 19.5A2.5 2.5 0 0 1 6.5 17H20'/%3E%3Cpath d='M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z'/%3E%3C/svg%3E");
}

/* Feedback icon - styled via CSS based on current theme */
.feedback-icon {
  display: inline-block;
  width: 24px;
  height: 24px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  background-image: url('@/assets/icons/feedback-icon.svg');
}

[data-theme="dark"] .feedback-icon {
  background-image: url('@/assets/icons/feedback-icon-dark.svg');
}

.feedback-link {
  text-decoration: none;
  color: inherit;
}

/* --- Projects Section --- */
.projects-section {
  margin-bottom: 8px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
  cursor: pointer;
  user-select: none;
}

.section-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #666;
}

.section-toggle {
  font-size: 0.75rem;
  color: #666;
}

.projects-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
}

.project-item {
  display: flex;
  align-items: center;
  padding: 6px 4px;
  font-size: 0.85rem;
  color: #1C1C1C;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-radius: 5px;
  gap: 6px;
}

.project-item:hover {
  background-color: #d3d3d3;
}

.project-item.selected {
  background-color: #E8F0FE;
  border-radius: 8px;
}

.project-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.project-title {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pin-indicator {
  flex-shrink: 0;
  font-size: 0.7rem;
}

.new-project-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 4px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.82rem;
  color: #666;
  border-radius: 5px;
  transition: background-color 0.2s ease;
}

.new-project-btn:hover {
  background-color: #d3d3d3;
  color: #1C1C1C;
}

.icon-sm {
  width: 16px;
  height: 16px;
}

/* --- Modal Styles --- */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--bg-primary, #fff);
  border-radius: 12px;
  padding: 24px;
  width: 420px;
  max-width: 90vw;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-title {
  margin: 0 0 20px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary, #1C1C1C);
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-secondary, #555);
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border-color, #ddd);
  border-radius: 8px;
  font-size: 0.9rem;
  background: var(--input-bg, #fff);
  color: var(--text-primary, #1C1C1C);
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #8ECAE6;
  box-shadow: 0 0 0 3px rgba(142, 202, 230, 0.2);
}

.form-textarea {
  resize: vertical;
  min-height: 60px;
  font-family: inherit;
}

.form-error {
  color: #e53e3e;
  font-size: 0.82rem;
  margin-bottom: 12px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.btn {
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: opacity 0.2s, background-color 0.2s;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-cancel {
  background: var(--hover-bg, #e4e4e4);
  color: var(--text-primary, #1C1C1C);
}

.btn-cancel:hover {
  background: #d0d0d0;
}

.btn-create {
  background: #8ECAE6;
  color: #1C1C1C;
}

.btn-create:hover:not(:disabled) {
  opacity: 0.85;
}

/* --- Project Detail / Documents --- */
.project-detail {
  padding: 4px 0 8px 26px;
}

.project-description {
  font-size: 0.72rem;
  color: var(--text-secondary, #888);
  font-style: italic;
  margin-bottom: 6px;
  line-height: 1.3;
  word-break: break-word;
}

.project-docs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.project-docs-label {
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--text-secondary, #888);
}

.upload-btn {
  font-size: 0.72rem;
  color: var(--text-secondary, #666);
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.upload-btn:hover {
  background-color: var(--hover-bg, #e4e4e4);
  color: var(--text-primary, #1C1C1C);
}

.upload-btn.uploading {
  opacity: 0.5;
  cursor: not-allowed;
}

.file-input-hidden {
  display: none;
}

.project-docs-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.project-doc-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 4px;
  font-size: 0.78rem;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.project-doc-item:hover {
  background-color: var(--hover-bg, #f0f0f0);
}

.doc-icon {
  font-size: 0.7rem;
  flex-shrink: 0;
}

.doc-name {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--text-primary, #1C1C1C);
}

.doc-delete-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary, #999);
  font-size: 0.9rem;
  padding: 0 2px;
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
}

.project-doc-item:hover .doc-delete-btn {
  opacity: 1;
}

.doc-delete-btn:hover {
  color: #e53e3e;
}

.project-docs-empty {
  font-size: 0.72rem;
  color: var(--text-secondary, #999);
  font-style: italic;
  padding: 2px 0;
}

.project-new-chat-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 5px 6px;
  background: var(--accent-light, #e8f4fd);
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.78rem;
  color: var(--text-primary, #1C1C1C);
  transition: background-color 0.2s;
  margin-bottom: 6px;
}

.project-new-chat-btn:hover {
  background: var(--accent, #8ECAE6);
}

.project-action-buttons {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
}

.project-action-buttons .project-new-chat-btn {
  flex: 1;
  margin-bottom: 0;
}

.project-artifacts-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 8px;
  background: var(--accent-light, #e8f4fd);
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.78rem;
  color: var(--text-primary, #1C1C1C);
  transition: background-color 0.2s;
  white-space: nowrap;
}

.project-artifacts-btn:hover {
  background: var(--accent, #8ECAE6);
}

.project-artifacts-btn svg {
  flex-shrink: 0;
}

.project-chats-section {
  margin-bottom: 6px;
}

.project-chat-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 4px;
  font-size: 0.78rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.project-chat-item:hover {
  background-color: var(--hover-bg, #f0f0f0);
}

.chat-remove-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary, #999);
  font-size: 0.9rem;
  padding: 0 2px;
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
}

.project-chat-item:hover .chat-remove-btn {
  opacity: 1;
}

.chat-remove-btn:hover {
  color: #e53e3e;
}

.icon-xs {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

/* --- Project Action Button & Context Menu --- */
.project-action-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary, #999);
  font-size: 0.85rem;
  padding: 0 4px;
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
  line-height: 1;
  letter-spacing: 1px;
}

.project-item:hover .project-action-btn {
  opacity: 1;
}

.project-context-menu {
  position: absolute;
  right: 0;
  top: 100%;
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-color, #ddd);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 140px;
  padding: 4px 0;
  overflow: hidden;
}

.context-menu-item {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 8px 12px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.82rem;
  color: var(--text-primary, #1C1C1C);
  transition: background-color 0.15s;
  text-align: left;
}

.context-menu-item:hover {
  background-color: var(--hover-bg, #f0f0f0);
}

/* Make project-item relative for context menu positioning */
.project-item {
  position: relative;
}

/* Archived projects toggle button */
.archived-toggle-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 6px 4px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.75rem;
  color: var(--text-secondary, #888);
  border-radius: 5px;
  transition: background-color 0.2s;
  margin-top: 4px;
}

.archived-toggle-btn:hover {
  background-color: var(--hover-bg, #e4e4e4);
  color: var(--text-primary, #1C1C1C);
}

/* Archived project items have a muted appearance */
.archived-project-item {
  opacity: 0.65;
}

.archived-project-item:hover {
  opacity: 0.85;
}

.archived-project-item .project-action-btn {
  opacity: 0.7;
}

.archived-project-item:hover .project-action-btn {
  opacity: 1;
}

/* --- Chat Kebab Menu Button --- */
.chat-kebab-btn {
  flex: 0 0 auto;
  opacity: 0;
  pointer-events: none;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  padding: 0 4px;
  margin-left: 4px;
  margin-right: 4px;
  transition: opacity 0.15s ease;
  line-height: 1;
  color: inherit;
  letter-spacing: 1px;
}

.conversation-item:hover .chat-kebab-btn,
.conversation-item:focus-within .chat-kebab-btn {
  opacity: 0.6;
  pointer-events: auto;
}

.chat-kebab-btn:hover {
  opacity: 1;
}

/* --- Chat Context Menu --- */
.chat-context-menu {
  position: absolute;
  right: 0;
  top: 100%;
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-color, #ddd);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 160px;
  padding: 4px 0;
  overflow: hidden;
}

/* --- Move to Project Modal --- */
.move-chat-name {
  font-size: 0.85rem;
  color: var(--text-secondary, #666);
  margin: 0 0 12px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.project-select-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 16px;
}

.project-select-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s;
  font-size: 0.9rem;
  color: var(--text-primary, #1C1C1C);
}

.project-select-item:hover {
  background-color: var(--hover-bg, #f0f0f0);
}

.project-select-item.current-project {
  background-color: var(--selected-bg, #E8F0FE);
  cursor: default;
}

.project-select-name {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.current-badge {
  font-size: 0.7rem;
  color: var(--text-secondary, #999);
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--hover-bg, #e4e4e4);
  flex-shrink: 0;
}

.project-select-empty {
  font-size: 0.85rem;
  color: var(--text-secondary, #999);
  text-align: center;
  padding: 20px;
  font-style: italic;
}

/* --- Delete Confirmation Modal --- */
.delete-warning {
  font-size: 0.9rem;
  color: var(--text-primary, #1C1C1C);
  margin: 0 0 8px 0;
  line-height: 1.4;
}

.delete-details {
  font-size: 0.82rem;
  color: var(--text-secondary, #666);
  margin: 0 0 16px 0;
  line-height: 1.4;
}

.btn-danger {
  background: #e53e3e;
  color: #fff;
}

.btn-danger:hover:not(:disabled) {
  background: #c53030;
}

/* --- Context Menu Danger Item --- */
.context-menu-item-danger {
  color: #e53e3e;
}

.context-menu-item-danger:hover {
  background-color: #fee2e2;
}

/* --- Empty Project State --- */
.project-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 8px;
  text-align: center;
}

.project-empty-icon {
  color: var(--text-secondary, #999);
  margin-bottom: 8px;
  opacity: 0.5;
}

.project-empty-text {
  margin: 0 0 4px 0;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--text-primary, #1C1C1C);
}

.project-empty-hint {
  margin: 0 0 12px 0;
  font-size: 0.72rem;
  color: var(--text-secondary, #999);
}

.project-empty-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.project-empty-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 500;
  transition: background-color 0.2s, opacity 0.2s;
}

.project-empty-btn-primary {
  background: #8ECAE6;
  color: #1C1C1C;
}

.project-empty-btn-primary:hover {
  opacity: 0.85;
}

.project-empty-btn-secondary {
  background: var(--hover-bg, #f0f0f0);
  color: var(--text-primary, #1C1C1C);
}

.project-empty-btn-secondary:hover {
  background: var(--accent-light, #e8f4fd);
}

.project-empty-btn-secondary.uploading {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>